import PropTypes from 'prop-types';
import styled from 'styled-components';
import Link from 'next/link';

import { config } from '_config';
import { theme } from '_constants/theme';

import * as Layout from '_components/atomic/layouts';
import JoinBanner from '../JoinBanner';

export const checkingDomainAndTrademarkAvailabilityContents = [
  'Understanding Domain Names',
  'Checking Domain Name Availability',
  'Understanding Trademarks',
  'Checking Trademark Availability',
  'Securing Your Domain Name and Trademark',
];

const StyledContainer = styled.div`
  position: relative;
`;

const StyledBlock = styled.div`
  position: relative;
  z-index: 1;

  ul {
    padding-top: ${theme.spaces.regular}px;

    li {
      margin-bottom: ${theme.spaces.regular}px;

      b {
        text-decoration: underline;
      }
    }
  }
`;

const StyledBackgroundImageContainer = styled.div`
  position: absolute;
  top: 5%;
  width: 100%;

  img {
    width: 100%;
  }
`;

const CheckingDomainAndTrademarkAvailabilityArticle = ({ idPrefix }) => {
  return (
    <StyledContainer>
      <Layout.Card.Text>
        Before you can establish your brand online, it&apos;s crucial to ensure
        that your desired domain name and trademark are available. This guide
        will walk you through the process of checking domain and trademark
        availability, highlighting the importance of each step and providing
        practical tips for securing your brand&apos;s online presence.
      </Layout.Card.Text>
      <StyledBlock id={`${idPrefix}-${0}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          1. Understanding Domain Names
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          What is a Domain Name?
        </Layout.Card.Text>
        <Layout.Card.Text>
          A domain name is the web address that users type into their browsers
          to visit your website. It typically consists of a unique name followed
          by a domain extension (e.g., .com, .org, .net)
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          Why Domain Name Availability Matters?
        </Layout.Card.Text>
        <Layout.Card.Text>
          Having a domain name that reflects your brand is essential for
          building a strong online presence. It helps customers find you easily
          and establishes credibility for your business.
        </Layout.Card.Text>
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${1}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          2. Checking Domain Name Availability
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          Using Domain Name Registrars
        </Layout.Card.Text>
        <Layout.Card.Text>
          To check the availability of a domain name, you can use domain name
          registrars like GoDaddy, Namecheap, or Google Domains. Simply enter
          your desired domain name into the search bar to see if it&nbsp;s
          available.
          <br />
          Considerations for Choosing a Domain Name:
          <ul>
            <li>Choose a domain name that is easy to remember and spell.</li>
            <li>Avoid using hyphens or numbers, as these can be confusing.</li>
            <li>
              Check for similar domain names to avoid confusion or trademark
              infringement.
            </li>
          </ul>
        </Layout.Card.Text>
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${2}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          3. Understanding Trademarks
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          What is a Trademark?
        </Layout.Card.Text>
        <Layout.Card.Text>
          A trademark is a legally registered symbol, word, or phrase that
          represents a company or product and distinguishes it from others in
          the market.
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          Why Trademark Availability Matters?
        </Layout.Card.Text>
        <Layout.Card.Text>
          Registering a trademark protects your brand from being used by others
          and gives you exclusive rights to use it in commerce.
        </Layout.Card.Text>
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${3}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          4. Checking Trademark Availability
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          Using the USPTO Database
        </Layout.Card.Text>
        <Layout.Card.Text>
          The United States Patent and Trademark Office (USPTO) maintains a
          database of registered trademarks. You can&nbsp;
          <Link href="https://trademark-genius.com/search">search</Link> this
          database to see if your desired trademark is available. Search USPTO
          database using&nbsp;
          <Link href="https://trademark-genius.com/">Trademark Genius</Link>.
          <br />
          Considerations for Choosing a Trademark:
          <ul>
            <li>
              Ensure that your&nbsp;
              <Link href="https://trademark-genius.com/">trademark</Link> is
              unique and not already in use by another company.
            </li>
            <li>
              Avoid using generic or descriptive terms that may be difficult to
              trademark.
            </li>
          </ul>
        </Layout.Card.Text>
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${4}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          5. Securing Your Domain Name and Trademark
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          Registering Your Domain Name?
        </Layout.Card.Text>
        <Layout.Card.Text>
          Once you&nbsp;ve confirmed that your domain name is available, you can
          register it through a domain registrar. Registering for multiple years
          can provide added security and peace of mind.
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          Registering Your Trademark?
        </Layout.Card.Text>
        <Layout.Card.Text>
          If your desired trademark is available, consider registering it with
          the USPTO to protect your brand identity. This process typically
          involves filing an application and paying a fee.
        </Layout.Card.Text>
      </StyledBlock>
      <Layout.Card.Text>
        Checking the availability of your domain name and trademark is a
        critical step in establishing your brand online. By following the steps
        outlined in this guide, you can ensure that your brand is unique,
        protected, and ready to make an impact in the digital world.
      </Layout.Card.Text>
      <Layout.Card.Text>
        For more guidance on securing your brand&apos;s online presence,
        visit&nbsp;
        <Link href="">Incorpified</Link>. Our experts can help you navigate the
        process of registering your domain name and trademark, ensuring that
        your brand is protected for years to come.
      </Layout.Card.Text>
      <JoinBanner />
      <StyledBackgroundImageContainer>
        <picture>
          <source
            srcSet={`${config.basePath}/images/blog-arrow-logo2.svg`}
            type="image/svg+xml"
          />
          <img
            src={`${config.basePath}/images/blog-arrow-logo2.svg`}
            alt="Background Logo"
          />
        </picture>
      </StyledBackgroundImageContainer>
    </StyledContainer>
  );
};

CheckingDomainAndTrademarkAvailabilityArticle.propTypes = {
  idPrefix: PropTypes.string,
};

export default CheckingDomainAndTrademarkAvailabilityArticle;
